import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDebounce } from "./hooks/useDebounce";
import { useGoogleMaps } from "./hooks/useGoogleMaps";
import Text from "../../../../../../shared/components/Text";
import { Autocomplete } from "@material-ui/lab";
import { useAddressFormFields } from "./hooks/useAddressFormFields.jsx";
import { AddressFormResetValues } from "../../constants/AddressFormResetValues.js";

export const MANUAL_OPTION_VALUE = "MANUAL";

export const GoogleMapsAutocomplete = ({
  name,
  apiKey,
  label,
  placeholder,
  hidden,
  language,
  debounceTime = 500,
}) => {
  const { palette, customBorderRadius } = useTheme();

  const {
    control,
    setValue,
    formState,
    trigger: triggerFormValidation,
    watch,
    reset,
  } = useFormContext();
  const { debounce } = useDebounce();
  const { load, retrievePredictions, geocodePlace } = useGoogleMaps();
  const { updateFormFieldsFromGeocode } = useAddressFormFields({
    setValue,
    trigger: triggerFormValidation,
  });

  const addressValue = watch("address.address");

  const [address, setAddress] = useState(addressValue ?? null);
  const [predictions, setPredictions] = useState(null);
  const [searching, setSearching] = useState(null);
  const [inputIsDirty, setInputIsDirty] = useState(false);
  const [updatingFields, setUpdatingFields] = useState(false);
  const NO_OPTIONS_TID = "NO_OPTIONS_TO_DISPLAY";

  useEffect(() => {
    load(apiKey, language);
  }, []);

  const getOptions = () => {
    if (searching || !address || !inputIsDirty) return [];

    const OPTION_NO_RESULTS = <Text tId={NO_OPTIONS_TID} />;

    return predictions && predictions.length > 0
      ? [...predictions.map((p) => p.description), MANUAL_OPTION_VALUE]
      : [OPTION_NO_RESULTS, MANUAL_OPTION_VALUE];
  };

  const onInputChange = (_, value) => {
    let isDirty = inputIsDirty;
    if (!isDirty && value !== formState.defaultValues?.address?.address) {
      isDirty = true;
      setInputIsDirty(isDirty);
    }

    if (!value || value.length <= 3) {
      setPredictions(null);
      setSearching(false);
      setAddress(null);
      reset(AddressFormResetValues);
      return;
    }

    setSearching(true);
    setAddress(value);
    debounce(async () => {
      try {
        const matches = await retrievePredictions(value);
        setPredictions(matches);
      } catch (err) {
        setPredictions(null);
      } finally {
        setSearching(false);
      }
    }, debounceTime);
  };

  const isSelectedValueInOptions = (option) =>
    getOptions().length > 0 && getOptions().includes(option);

  const onSelect = async (_, option) => {
    if (
      option === undefined ||
      option === null ||
      option === "" ||
      !isSelectedValueInOptions(option)
    )
      return;

    if (option === MANUAL_OPTION_VALUE) {
      setUpdatingFields(false);
      setAddress(MANUAL_OPTION_VALUE);
      reset({
        address: {
          ...AddressFormResetValues.address,
          address: MANUAL_OPTION_VALUE,
        },
      });
      return;
    }

    const selectedOption = predictions?.find((p) => p.description === option);
    if (!selectedOption) return;

    setTimeout(async () => {
      try {
        const res = await geocodePlace(selectedOption.place_id);
        updateFormFieldsFromGeocode(res);
        setPredictions(null);
      } catch (error) {
        console.log(error);
      } finally {
        setUpdatingFields(false);
      }
    }, 800);
  };

  const renderOption = (option) => {
    if (option !== MANUAL_OPTION_VALUE) {
      return option;
    }
    return (
      <Box
        display={"block"}
        borderTop={`1px solid ${palette.grey[300]}`}
        mt={"-6px"}
        boxSizing={"border-box"}
        width={"100%"}
      >
        <Box
          display={"flex"}
          color={palette.text.primary}
          flexDirection={"row"}
          alignItems={"center"}
          mt={1}
          gridGap={8}
        >
          <Typography variant="body1">
            <Text tId={"COMPLETE_ADDRESS_MANUALLY"} />
          </Typography>
          <i className="material-icons-outlined" style={{ fontSize: 18 }}>
            edit
          </i>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box
        borderRadius={customBorderRadius.paper}
        display={(address === MANUAL_OPTION_VALUE || hidden) && "none"}
      >
        <Controller
          name={name}
          control={control}
          render={(props) => {
            return (
              <Autocomplete
                freeSolo
                loading={!!searching}
                loadingText={<Text tId={"LOADING"} />}
                autoComplete
                autoFocus
                clearOnBlur
                selectOnFocus
                options={getOptions()}
                value={props.value ?? null}
                onChange={async (_, value) => {
                  if (isSelectedValueInOptions(value)) {
                    setUpdatingFields(true);
                  }
                  onSelect(_, value);
                }}
                onInputChange={onInputChange}
                getOptionLabel={(option) =>
                  option === MANUAL_OPTION_VALUE ? "" : option
                }
                renderOption={renderOption}
                noOptionsText={<Text tId={NO_OPTIONS_TID} />}
                getOptionDisabled={(option) =>
                  option?.props?.tId === NO_OPTIONS_TID
                }
                filterOptions={(options) =>
                  options.length === 0 ? [] : options
                }
                renderInput={(params) => {
                  return (
                    <TextField
                      key={props.value || "key"}
                      autoFocus
                      {...params}
                      error={props.error}
                      label={label}
                      placeholder={placeholder}
                    />
                  );
                }}
              />
            );
          }}
        />
        <Box mt={1}>
          {updatingFields ? (
            <Box display={"flex"} alignItems="center" gridGap={6}>
              <CircularProgress color="primary" size={16} />
              <Typography variant="body1" color="textSecondary">
                <Text tId={"UPDATING_FIELDS"} />
                ...
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" color="textSecondary">
              <Text tId={"ADDRESS_CONTINUE_MESSAGE"} />
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};
